import styled, { css, keyframes } from 'styled-components';
import { Drawer } from '@mui/material';

import colors, { getContrastSafeColor } from '../../../styles/colors';
import { checkoutPagesFontStyles } from '../../../styles/text';
import { STICKER_CANVAS_WIDTH, STICKER_MARGIN } from '../core/renderStory';
import { STICKER_WIDTH } from '../core/stickerFrames';

/*
  The flow takes the coach's checkout theme and color, so it reads as the page the client just paid on. The
  colors are CSS variables set once on the flow, so every piece below follows any coach without props.
*/
const getFlowColors = ({ brandColor, isDarkTheme }) => {
  const page = isDarkTheme ? colors.shades.dark1 : colors.base.alpha;
  const text = isDarkTheme ? colors.base.alpha : colors.base.beta;
  return {
    '--story-page': page,
    '--story-text': text,
    '--story-muted': isDarkTheme ? colors.shades.gamma14 : colors.shades.gamma10,
    '--story-surface': isDarkTheme ? colors.shades.dark5 : colors.shades.gamma4,
    '--story-border': isDarkTheme ? colors.shades.dark2 : colors.shades.dark4,
    '--story-accent': brandColor,
    '--story-on-accent': getContrastSafeColor(brandColor, colors.base.beta, colors.base.alpha),
    '--story-accent-text': getContrastSafeColor(page, brandColor, text),
    '--story-glow': colors.addAlpha(brandColor, 0.24),
  };
};

const pop = keyframes`
  0% { transform: scale(0.6) rotate(-8deg); opacity: 0; }
  60% { transform: scale(1.04) rotate(3deg); opacity: 1; }
  100% { transform: scale(1) rotate(2deg); }
`;

const fall = keyframes`
  from { transform: translateY(-40px) rotate(0); }
  to { transform: translateY(110vh) rotate(540deg); }
`;

const fadeInOut = keyframes`
  0% { opacity: 0; transform: translate(-50%, 8px); }
  12%, 85% { opacity: 1; transform: translate(-50%, 0); }
  100% { opacity: 0; transform: translate(-50%, 0); }
`;

const reducedMotion = (rules) => css`
  @media (prefers-reduced-motion: reduce) {
    ${rules};
  }
`;

// Its own stacking context, so the confetti falls behind the content.
const FlowContainer = styled.section`
  ${checkoutPagesFontStyles};
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 420px;
  box-sizing: border-box;
  padding: 16px 16px 32px;
  color: var(--story-text);
`;

const Intro = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
`;

const TaskTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px 7px ${({ $hasBadge }) => ($hasBadge ? 8 : 10)}px;
  border-radius: 999px;
  background-color: var(--story-surface);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
`;

const TagDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--story-accent);
`;

const CheckBadge = styled.span`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  box-sizing: border-box;
  border-radius: 50%;
  background-color: var(--story-accent);
  color: var(--story-on-accent);
`;

const EmptyBadge = styled(CheckBadge)`
  border: 1.5px solid var(--story-border);
  background-color: transparent;
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${({ $size }) => $size}px;
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.025em;
`;

const Body = styled.p`
  margin: 0;
  max-width: 320px;
  font-size: 15px;
  line-height: 1.42;
  color: var(--story-muted);
`;

/*
  The sticker's image carries a margin for the parts that stick out, so it is laid out by the sticker's own width
  and the margin is taken back.
*/
const stickerImageStyles = css`
  display: block;
  width: ${({ $width }) => ($width * STICKER_CANVAS_WIDTH) / STICKER_WIDTH}px;
  max-width: none;
  margin: ${({ $width }) => -($width * STICKER_MARGIN) / STICKER_WIDTH}px;
  filter: drop-shadow(0 12px 14px rgba(0, 0, 0, 0.3)) drop-shadow(0 3px 5px rgba(0, 0, 0, 0.15));
`;

const StickerImage = styled.img`
  ${stickerImageStyles};
  transform: rotate(${({ $tilt }) => $tilt}deg);
`;

const PoppedSticker = styled.img`
  ${stickerImageStyles};
  animation: ${pop} 0.7s cubic-bezier(0.2, 0.9, 0.3, 1.2) both;
  ${reducedMotion('animation: none; transform: rotate(2deg);')}
`;

const StickerStage = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 0;
`;

// A file input kept out of sight, with its label showing the keyboard focus.
const hiddenInputStyles = css`
  &:has(input:focus-visible) {
    outline: 2px solid var(--story-text);
    outline-offset: 6px;
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }
`;

// The sticker is the photo picker: a tap on it opens the file picker, or on a phone the photo sheet.
const StickerPicker = styled.label`
  ${hiddenInputStyles};
  position: relative;
  align-self: center;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: none;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--story-text);
    outline-offset: 6px;
  }
`;

const StickerPlaceholder = styled.div`
  width: ${({ $width }) => $width}px;
  aspect-ratio: 7 / 10;
`;

const Picker = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Tabs = styled.div`
  display: flex;
  align-self: center;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background-color: var(--story-surface);
`;

const Tab = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 9px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${({ 'aria-selected': isSelected }) => (isSelected ? 'var(--story-text)' : 'transparent')};
  color: ${({ 'aria-selected': isSelected }) => (isSelected ? 'var(--story-page)' : 'var(--story-muted)')};
`;

// Scrolls sideways when the options overflow, and centers them when they fit.
const OptionRow = styled.div`
  display: flex;
  gap: 12px;
  margin: 0 -16px;
  padding: 8px 16px 6px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &::before,
  &::after {
    content: '';
    margin: auto;
  }
`;

const Option = styled.button`
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: ${({ 'aria-pressed': isPressed }) => (isPressed ? 700 : 500)};
  color: var(--story-text);
  opacity: ${({ 'aria-pressed': isPressed }) => (isPressed ? 1 : 0.75)};
  cursor: pointer;
`;

const OptionThumb = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 66px;
  height: 66px;
  overflow: hidden;
  border-radius: 16px;
  background-color: var(--story-surface);
  box-shadow: ${({ $isSelected }) => ($isSelected
    ? '0 0 0 2px var(--story-page), 0 0 0 4px var(--story-accent)'
    : '0 0 0 1px var(--story-border)')};

  img {
    width: 100%;
    height: 100%;
    object-fit: ${({ $fit }) => $fit};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const buttonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 999px;
  font-family: inherit;
  cursor: pointer;
  text-decoration: none;

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
    transform: none;
  }
`;

const primaryStyles = css`
  ${buttonStyles};
  height: 56px;
  border: none;
  background-color: var(--story-accent);
  color: var(--story-on-accent);
  font-size: 16.5px;
  font-weight: 700;
`;

const secondaryStyles = css`
  ${buttonStyles};
  height: 52px;
  border: 1.5px solid var(--story-border);
  background-color: transparent;
  color: var(--story-text);
  font-size: 15.5px;
  font-weight: 600;
`;

const PhotoSheet = styled(Drawer)`
  .MuiDrawer-paper {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px 16px 24px;
    border-radius: 20px 20px 0 0;
    background-color: var(--story-page);
    color: var(--story-text);
  }
`;

const PhotoSheetTitle = styled.p`
  margin: 0 0 4px;
  font-size: 17px;
  font-weight: 700;
  text-align: center;
`;

const PhotoSheetOption = styled.label`
  ${secondaryStyles};
  ${hiddenInputStyles};
  position: relative;
  justify-content: flex-start;
  padding: 0 20px;

  &:has(input:focus-visible) {
    outline-offset: 2px;
  }
`;

const PrimaryButton = styled.button`
  ${primaryStyles};
`;

const SecondaryButton = styled.button`
  ${secondaryStyles};
`;

const PrimaryLink = styled.a`
  ${primaryStyles};
`;

const SecondaryLink = styled.a`
  ${secondaryStyles};
`;

const TextButton = styled.button`
  align-self: center;
  min-height: 40px;
  padding: 0 16px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 14.5px;
  font-weight: 500;
  color: var(--story-muted);
  cursor: pointer;
`;

const StatusText = styled.p`
  margin: 0;
  font-size: 14px;
  text-align: center;
  color: var(--story-muted);
`;

const Proof = styled.p`
  margin: 0 0 4px;
  font-size: 13.5px;
  line-height: 1.35;
  text-align: ${({ $align }) => $align || 'center'};
  color: var(--story-muted);

  strong {
    color: var(--story-text);
  }
`;

const Toast = styled.div`
  position: fixed;
  top: 64px;
  left: 50%;
  z-index: 11;
  padding: 10px 16px;
  border-radius: 999px;
  background-color: var(--story-text);
  color: var(--story-page);
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  animation: ${fadeInOut} 1.6s ease forwards;
`;

// Behind the content of its container, which is a stacking context of its own.
const ConfettiLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
  ${reducedMotion('display: none;')}
`;

const ConfettiPiece = styled.span`
  position: absolute;
  top: -30px;
  opacity: 0.9;
  animation: ${fall} linear infinite;
`;

const AppIntro = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding-top: 8px;
`;

const AppIntroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Checklist = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 6px 18px;
  border-radius: 22px;
  background-color: var(--story-surface);
  list-style: none;
`;

const ChecklistItem = styled.li`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;

  & + & {
    border-top: 1px solid var(--story-border);
  }
`;

const ChecklistText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 13px;
  color: var(--story-muted);

  strong {
    font-size: 15px;
    font-weight: 600;
    color: var(--story-text);
  }
`;

const AppBadge = styled.span`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background-color: var(--story-accent);
  color: var(--story-on-accent);
`;

const Footnote = styled.p`
  margin: 6px 0 0;
  font-size: 12.5px;
  text-align: center;
  color: var(--story-muted);
`;

// A computer's page: the sticker on the left, the code to carry on from a phone on the right.
const HandoffLayout = styled.section`
  ${checkoutPagesFontStyles};
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  width: calc(100% - 48px);
  max-width: 1200px;
  min-height: 600px;
  margin: 24px;
  overflow: hidden;
  border-radius: 24px;
  border: 1px solid var(--story-border);
  color: var(--story-text);

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const HandoffStage = styled.div`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 420px;
  background: radial-gradient(circle at 50% 45%, var(--story-glow) 0, transparent 70%), var(--story-surface);
`;

const HandoffPanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 22px;
  padding: 48px 56px;
  background-color: var(--story-page);

  ${Body} {
    max-width: 440px;
    font-size: 16px;
  }

  ${Proof} {
    font-size: 15px;
  }
`;

const HandoffTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  button {
    flex: none;
    white-space: nowrap;
  }
`;

const QrCard = styled.section`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px;
  border-radius: 20px;
  background-color: var(--story-surface);
`;

// Always dark on white, whatever the theme, with about four modules of white around the code (the QR spec's quiet
// zone), so every phone camera reads it.
const QrFrame = styled.div`
  display: flex;
  flex: none;
  padding: 12px;
  border-radius: 12px;
  background-color: ${colors.base.alpha};
`;

const QrText = styled.p`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: var(--story-muted);

  strong {
    font-size: 17px;
    font-weight: 700;
    color: var(--story-text);
  }
`;

// Shows the story full size, so an iPhone's press-and-hold menu can save it to Photos.
const SaveView = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px 16px;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.85);
  color: ${colors.base.alpha};
  text-align: center;

  img {
    max-height: 70vh;
    max-width: 100%;
    border-radius: 12px;
  }

  button {
    width: auto;
    padding: 0 28px;
    border-color: ${colors.base.alpha};
    color: ${colors.base.alpha};
  }
`;

export {
  getFlowColors,
  FlowContainer,
  Intro,
  TaskTag,
  TagDot,
  CheckBadge,
  EmptyBadge,
  Title,
  Body,
  StickerImage,
  PoppedSticker,
  StickerStage,
  StickerPicker,
  PhotoSheet,
  PhotoSheetTitle,
  PhotoSheetOption,
  StickerPlaceholder,
  Picker,
  Tabs,
  Tab,
  OptionRow,
  Option,
  OptionThumb,
  Actions,
  PrimaryButton,
  SecondaryButton,
  PrimaryLink,
  SecondaryLink,
  TextButton,
  StatusText,
  Proof,
  Toast,
  ConfettiLayer,
  ConfettiPiece,
  AppIntro,
  AppIntroText,
  Checklist,
  ChecklistItem,
  ChecklistText,
  AppBadge,
  Footnote,
  HandoffLayout,
  HandoffStage,
  HandoffPanel,
  HandoffTop,
  QrCard,
  QrFrame,
  QrText,
  SaveView,
};
